import { Link } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { FieldError, authFieldWrapClass } from '@/features/auth/components/FieldError';
import { LoginIdentifierField } from '@/features/auth/components/LoginIdentifierField';
import { LoginOtpFields } from '@/features/auth/components/LoginOtpFields';
import type {
  LoginFormValues,
  LoginMethod,
} from '@/features/auth/utils/loginIdentifier';
import type { MobileConfig } from '@/features/auth/utils/mobileNumber';

type LoginFormProps = {
  form: UseFormReturn<LoginFormValues>;
  loginMethod: LoginMethod;
  otpSent: boolean;
  otpDigits: string[];
  otpError: string;
  showPassword: boolean;
  phoneConfig: MobileConfig;
  isPhoneInput: boolean;
  isLoading: boolean;
  submitLabel: string;
  resendSeconds: number;
  onSwitchMethod: (method: LoginMethod) => void;
  onOtpChange: (next: string[]) => void;
  onTogglePassword: () => void;
  onSendOtp: () => void;
  onChangeIdentifier: () => void;
  onSubmit: (values: LoginFormValues) => void;
};

export function LoginForm({
  form,
  loginMethod,
  otpSent,
  otpDigits,
  otpError,
  showPassword,
  phoneConfig,
  isPhoneInput,
  isLoading,
  submitLabel,
  resendSeconds,
  onSwitchMethod,
  onOtpChange,
  onTogglePassword,
  onSendOtp,
  onChangeIdentifier,
  onSubmit,
}: LoginFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <div className="signup-form-align flex w-full min-w-0 flex-col">
      <header className="mb-5 sm:mb-6">
        <h2
          className="m-0 text-[30px] font-extrabold leading-tight tracking-tight text-[#2E2A54]"
          style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
        >
          Sign in
        </h2>
        <p className="mt-1.5 mb-0 text-[14px] leading-snug text-[#736F8E]">
          Use your Screenista advertiser account.
        </p>
      </header>

      {/* Modified by: Ishan | Decreased top toggle height to 44px (36px inner tabs) with reference gradient */}
      <div className="mb-5 grid w-full grid-cols-2 rounded-full bg-[#E8E5F4] p-[4px] text-center text-[13.5px] font-bold">
        {(
          [
            { key: 'otp', label: 'OTP' },
            { key: 'password', label: 'Password' },
          ] as const
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onSwitchMethod(tab.key)}
            className={`flex h-[36px] items-center justify-center rounded-full border-none transition-all cursor-pointer ${
              loginMethod === tab.key
                ? 'bg-gradient-to-r from-[#3B62BD] to-[#7653A8] text-white shadow-sm'
                : 'bg-transparent text-[#736F8E] hover:text-[#2E2A54]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full min-w-0 flex-col gap-1 text-left"
      >
        <LoginIdentifierField
          form={form}
          loginMethod={loginMethod}
          otpSent={otpSent}
          phoneConfig={phoneConfig}
          isPhoneInput={isPhoneInput}
        />

        {loginMethod === 'password' ? (
          <div className="w-full min-w-0">
            <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
              Password
            </label>
            <div className={`relative ${authFieldWrapClass(Boolean(errors.password))}`}>
              <div className="pointer-events-none shrink-0 pl-3.5 text-[#8156B4]">
                <Lock size={18} strokeWidth={1.8} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                maxLength={20}
                className="h-full min-w-0 flex-1 border-none bg-transparent py-0 pr-10 pl-2.5 text-sm text-[#2E2A54] outline-none placeholder:text-[#A9A6BE]"
                {...register('password', {
                  required:
                    loginMethod === 'password' ? 'Password is required' : false,
                })}
              />
              <button
                type="button"
                onClick={onTogglePassword}
                className="absolute top-1/2 right-3 -translate-y-1/2 border-none bg-transparent p-1 text-[#6A6688] hover:text-[#3A3660]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <FieldError message={errors.password?.message} />
          </div>
        ) : null}

        {loginMethod === 'otp' && otpSent ? (
          <LoginOtpFields
            otpDigits={otpDigits}
            otpError={otpError}
            onOtpChange={onOtpChange}
          />
        ) : null}

        <div className="mt-2 flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 select-none">
            <input
              type="checkbox"
              className="signup-check h-[17px] w-[17px] min-w-[17px] cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-[#C7D1CF] transition-[background,border-color]"
              {...register('rememberMe')}
            />
            <span className="text-[13px] text-[#4A4670]">Stay signed in</span>
          </label>

          {loginMethod === 'password' ? (
            <Link
              to="/forgot-password"
              className="text-[13px] font-semibold text-[#3B66B8] no-underline hover:text-[#8156B4]"
            >
              Forgot password?
            </Link>
          ) : otpSent ? (
            <button
              type="button"
              onClick={onChangeIdentifier}
              className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#3B66B8] hover:text-[#8156B4]"
            >
              Change Email
            </button>
          ) : (
            <span />
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="signup-btn mt-5 flex h-[44px] w-full items-center justify-center rounded-[14px] border-none text-[15px] font-bold text-white transition-all hover:enabled:-translate-y-px active:enabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {isLoading ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            submitLabel
          )}
        </button>

        {loginMethod === 'otp' && otpSent ? (
          <div className="mt-2 text-center text-[13px] text-[#6A6688]">
            {resendSeconds > 0 ? (
              <p className="m-0">
                Resend in{' '}
                <span
                  className="font-semibold text-[#8156B4]"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {Math.floor(resendSeconds / 60)}:
                  {String(resendSeconds % 60).padStart(2, '0')}
                </span>
              </p>
            ) : (
              <button
                type="button"
                onClick={onSendOtp}
                className="border-none bg-transparent p-0 font-semibold text-[#3B66B8] hover:text-[#8156B4]"
              >
                Resend OTP
              </button>
            )}
          </div>
        ) : null}
      </form>

      <p className="mt-5 text-center text-[13px] leading-none text-[#736F8E]">
        New to Screenista?{' '}
        <Link
          to="/signup"
          className="font-bold text-[#2E2A54] no-underline hover:text-[#3B62BD]"
        >
          Create account
        </Link>
      </p>
    </div>
  );
}









